import { isBefore, isSameDay, parseISO, subDays } from 'date-fns'
import { toJS } from 'mobx'
import { observer } from 'mobx-react'
import { Fragment, useCallback, useMemo, useRef, useState } from 'react'
import { useAsyncCallback } from 'react-async-hook'

import { NotificationType } from '@avantstay/arriere-clients/dist/arriereBackoffice'
import {
  Alert,
  Button,
  FlexContainer,
  formatDate,
  Loading,
  TabList,
  useOnClickOutside,
} from '@avantstay/backoffice-core'
import { IconCheck, IconClose, IconEmptyNotifications } from '@avantstay/backoffice-vectors'

import {
  getNotificationCategory,
  NotificationCategory,
  NotificationProps,
  TabOptions,
} from '../../OperationsNotification.types'
import { operationsNotificationStore } from '../../OperationsNotificationStore'
import { NotificationCard } from '../NotificationCard'
import * as S from './NotificationModal.styles'

const _NotificationModal = () => {
  const modalRef = useRef<HTMLDivElement | null>(null)
  const [tabIndex, setTabIndex] = useState(TabOptions.all)

  const {
    notifications,
    setShowNotificationModal,
    showNotificationModal,
    readNotification,
    hasUnreadNotifications,
  } = operationsNotificationStore

  const parsedNotifications = useMemo(() => {
    if (!notifications.value) return {}

    return notifications.value
      ?.slice()
      .sort((a, b) => {
        return isBefore(parseISO(a.createdAt), parseISO(b.createdAt)) ? 1 : -1
      })
      .filter(notification => {
        switch (tabIndex) {
          case TabOptions.mentions:
            return (
              getNotificationCategory(notification.notificationType) ===
              NotificationCategory.mention
            )
          case TabOptions.assignedToMe:
            return notification.notificationType === NotificationType.caseAssignedToVendor
          case TabOptions.unread:
            return !notification.readAt
          default:
            return true
        }
      })
      .reduce(
        (acc, notification: NotificationProps) => {
          const createdAt = formatDate(notification.createdAt, 'yyyy-MM-dd')
          if (!acc[createdAt]) {
            acc[createdAt] = []
          }
          acc[createdAt].push(toJS(notification))
          return acc
        },
        {} as Record<string, NotificationProps[]>,
      )
  }, [notifications.value, tabIndex])

  const handleMarkAllAsRead = useAsyncCallback(async () => {
    const notificationIds = notifications.value
      ?.filter(notification => !notification.readAt)
      .map(notification => notification.id)
    if (notificationIds && notificationIds.length > 0) {
      await readNotification(notificationIds, () => {
        notifications.silentRefresh()
        Alert.Success('All notifications marked as read')
      })
    }
  })

  useOnClickOutside(modalRef, () => {
    setShowNotificationModal(false)
  })

  const handleChangeTabIndex = (tab: TabOptions) => {
    if (tab === tabIndex) return
    setTabIndex(tab)
  }

  const parseLabel = (day: string, notifications: NotificationProps[]) => {
    let label = ''
    const isToday = isSameDay(new Date(), parseISO(day))
    const isYesterday = isSameDay(subDays(new Date(), 1), parseISO(day))

    if (isToday) {
      label = 'Today'
    } else if (isYesterday) {
      label = 'Yesterday'
    } else {
      label = formatDate(parseISO(day), 'MMM dd, yyyy')
    }

    const unreadNotifications = notifications.filter(notification => !notification.readAt)
    return label + (unreadNotifications.length > 0 ? ` ・NEW (${unreadNotifications.length})` : '')
  }

  const renderContent = useCallback(() => {
    const notifications = Object.entries(parsedNotifications)
    if (notifications.length === 0) {
      return (
        <FlexContainer direction="column" justify="center" alignItems="center" height="100%">
          <IconEmptyNotifications />
          <S.EmptyTitle>You're up to date!</S.EmptyTitle>
          <S.EmptyDescription>You don’t have any notifications for now.</S.EmptyDescription>
        </FlexContainer>
      )
    }

    return (
      <S.StyledTabPanel>
        {Object.entries(parsedNotifications)?.map(([day, notifications]) => (
          <Fragment key={day}>
            <S.StyledDayLabel>{parseLabel(day, notifications)}</S.StyledDayLabel>
            {notifications.map(notification => (
              <NotificationCard key={notification.id} notification={notification} />
            ))}
          </Fragment>
        ))}
      </S.StyledTabPanel>
    )
  }, [parsedNotifications])

  if (!showNotificationModal) return null

  return (
    <S.NotificationWrapper ref={modalRef}>
      <S.ModalHeader>
        <S.Title>Notifications</S.Title>
        {hasUnreadNotifications ? (
          <S.ReadAllNotificationsButton
            onClick={handleMarkAllAsRead.execute}
            loading={handleMarkAllAsRead.loading}
          >
            <IconCheck />
            <span>Mark all as read</span>
          </S.ReadAllNotificationsButton>
        ) : (
          <div />
        )}
        <Button.FlatNeutral onClick={() => setShowNotificationModal(false)}>
          <IconClose width={20} height={20} />
        </Button.FlatNeutral>
      </S.ModalHeader>

      <S.StyledTabs index={tabIndex} onChange={handleChangeTabIndex}>
        <TabList>
          <S.StyledTab>All</S.StyledTab>
          <S.StyledTab>Mentions</S.StyledTab>
          <S.StyledTab>Assigned to me</S.StyledTab>
          <S.StyledTab>Unread</S.StyledTab>
        </TabList>
        <S.StyledTabPanels>
          {notifications.loading ? (
            <S.StyledTabPanel>
              <Loading style={{ marginTop: 20 }} />
            </S.StyledTabPanel>
          ) : (
            renderContent()
          )}
        </S.StyledTabPanels>
      </S.StyledTabs>
    </S.NotificationWrapper>
  )
}

export const NotificationModal = observer(_NotificationModal)
